import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';

/**
 * Premium AnimatedSection Component
 * Luxury-grade intersection observer animations with sophisticated easing and performance optimization
 * Designed for high-end developer experiences
 */
const AnimatedSection = ({ 
  children, 
  animation = "fadeUp", 
  delay = 0, 
  duration = 800,
  className = "",
  threshold = 0.15,
  triggerOnce = true,
  stagger = 0,
  easing = "luxury",
  intensity = "medium"
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasTriggered, setHasTriggered] = useState(false);
  const elementRef = useRef(null);
  const timeoutRef = useRef(null);

  // Luxury easing curves for premium feel
  const easingCurves = {
    luxury: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',        // Sophisticated ease-out
    premium: 'cubic-bezier(0.4, 0, 0.2, 1)',               // Material design inspired
    elegant: 'cubic-bezier(0.165, 0.84, 0.44, 1)',         // Smooth elegance
    refined: 'cubic-bezier(0.19, 1, 0.22, 1)',             // Refined acceleration
    architectural: 'cubic-bezier(0.77, 0, 0.175, 1)',      // Bold architectural
    smooth: 'ease-out'
  };

  // Animation intensity levels
  const intensityLevels = {
    subtle: { distance: 20, scale: 0.98, blur: 2 },
    medium: { distance: 40, scale: 0.95, blur: 4 },
    bold: { distance: 60, scale: 0.92, blur: 6 },
    dramatic: { distance: 80, scale: 0.9, blur: 8 }
  };

  // Premium animation presets - optimized with useMemo
  const animationStyles = useMemo(() => {
    const intensity_config = intensityLevels[intensity] || intensityLevels.medium;
    
    const animations = {
      fadeUp: {
        initial: { 
          opacity: 0, 
          transform: `translateY(${intensity_config.distance}px)`,
          filter: `blur(${intensity_config.blur}px)`
        },
        animate: { 
          opacity: 1, 
          transform: 'translateY(0px)',
          filter: 'blur(0px)'
        }
      },
      fadeDown: {
        initial: { 
          opacity: 0, 
          transform: `translateY(-${intensity_config.distance}px)`,
          filter: `blur(${intensity_config.blur}px)`
        },
        animate: { 
          opacity: 1, 
          transform: 'translateY(0px)',
          filter: 'blur(0px)'
        }
      },
      fadeLeft: {
        initial: { 
          opacity: 0, 
          transform: `translateX(-${intensity_config.distance}px)`,
          filter: `blur(${intensity_config.blur}px)`
        },
        animate: { 
          opacity: 1, 
          transform: 'translateX(0px)',
          filter: 'blur(0px)'
        }
      },
      fadeRight: {
        initial: { 
          opacity: 0, 
          transform: `translateX(${intensity_config.distance}px)`,
          filter: `blur(${intensity_config.blur}px)`
        },
        animate: { 
          opacity: 1, 
          transform: 'translateX(0px)',
          filter: 'blur(0px)'
        }
      },
      scaleIn: {
        initial: { 
          opacity: 0, 
          transform: `scale(${intensity_config.scale})`,
          filter: `blur(${intensity_config.blur}px)`
        },
        animate: { 
          opacity: 1, 
          transform: 'scale(1)',
          filter: 'blur(0px)'
        }
      },
      scaleUp: {
        initial: { 
          opacity: 0, 
          transform: `scale(${intensity_config.scale}) translateY(${intensity_config.distance}px)`,
          filter: `blur(${intensity_config.blur}px)`
        },
        animate: { 
          opacity: 1, 
          transform: 'scale(1) translateY(0px)',
          filter: 'blur(0px)'
        }
      },
      slideUpFade: {
        initial: { 
          opacity: 0, 
          transform: `translateY(${intensity_config.distance}px) scale(${intensity_config.scale})`,
          filter: `blur(${intensity_config.blur}px)`
        },
        animate: { 
          opacity: 1, 
          transform: 'translateY(0px) scale(1)',
          filter: 'blur(0px)'
        }
      },
      revealUp: {
        initial: { 
          opacity: 0, 
          transform: `translateY(${intensity_config.distance * 1.5}px) rotateX(15deg)`,
          transformOrigin: 'center bottom',
          filter: `blur(${intensity_config.blur}px)`
        },
        animate: { 
          opacity: 1, 
          transform: 'translateY(0px) rotateX(0deg)',
          transformOrigin: 'center bottom',
          filter: 'blur(0px)'
        }
      },
      luxuryFade: {
        initial: { 
          opacity: 0, 
          transform: `translateY(${intensity_config.distance}px) scale(${intensity_config.scale}) rotateY(5deg)`,
          transformOrigin: 'center center',
          filter: `blur(${intensity_config.blur}px) brightness(0.8)`
        },
        animate: { 
          opacity: 1, 
          transform: 'translateY(0px) scale(1) rotateY(0deg)',
          transformOrigin: 'center center',
          filter: 'blur(0px) brightness(1)'
        }
      },
      architecturalReveal: {
        initial: { 
          opacity: 0, 
          transform: `translateY(${intensity_config.distance}px) perspective(1000px) rotateX(20deg)`,
          transformOrigin: 'center top',
          filter: `blur(${intensity_config.blur}px)`
        },
        animate: { 
          opacity: 1, 
          transform: 'translateY(0px) perspective(1000px) rotateX(0deg)',
          transformOrigin: 'center top',
          filter: 'blur(0px)'
        }
      }
    };

    return animations[animation] || animations.fadeUp;
  }, [animation, intensity]);

  // Enhanced intersection observer with performance optimization
  useEffect(() => {
    const currentElement = elementRef.current;
    if (!currentElement) return;

    // Use more sophisticated intersection observer options for luxury experience
    const observerOptions = {
      threshold: Array.from({length: 20}, (_, i) => i * 0.05), // Multiple thresholds for smoother triggers
      rootMargin: '-50px 0px -50px 0px', // Trigger slightly before element is visible
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        // Only trigger if element is sufficiently visible
        if (entry.isIntersecting && entry.intersectionRatio >= threshold) {
          if (triggerOnce && hasTriggered) return;
          
          // Clear any existing timeout
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
          }
          
          // Apply stagger delay for luxury sequencing
          const totalDelay = delay + stagger;
          
          timeoutRef.current = setTimeout(() => {
            setIsVisible(true);
            setHasTriggered(true);
            
            // Performance optimization: disconnect observer after trigger if triggerOnce is true
            if (triggerOnce) {
              observer.disconnect();
            }
          }, totalDelay);
        } else if (!triggerOnce && !entry.isIntersecting) {
          // Re-trigger animation when element leaves viewport (if triggerOnce is false)
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
          }
          setIsVisible(false);
        }
      },
      observerOptions
    );

    observer.observe(currentElement);

    // Cleanup function
    return () => {
      observer.disconnect();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [threshold, delay, stagger, triggerOnce, hasTriggered]);


  const selectedEasing = easingCurves[easing] || easingCurves.luxury;

  // Render with premium styling
  return (
    <div
      ref={elementRef}
      className={`transform-gpu will-change-transform ${className}`}
      style={{
        opacity: isVisible ? animationStyles.animate.opacity : animationStyles.initial.opacity,
        transform: isVisible ? animationStyles.animate.transform : animationStyles.initial.transform,
        transformOrigin: animationStyles.initial.transformOrigin || animationStyles.animate.transformOrigin || 'center center',
        filter: isVisible ? (animationStyles.animate.filter || 'none') : (animationStyles.initial.filter || 'none'),
        transition: `all ${duration}ms ${selectedEasing}`,
        // Hardware acceleration for smooth performance
        backfaceVisibility: 'hidden',
        perspective: '1000px',
      }}
    >
      {children}
    </div>
  );
};

// Higher-order component for easy stagger implementation
export const StaggeredSection = ({ children, staggerDelay = 100, ...props }) => {
  return (
    <div>
      {React.Children.map(children, (child, index) => (
        <AnimatedSection
          {...props}
          stagger={index * staggerDelay}
          key={index}
        >
          {child}
        </AnimatedSection>
      ))}
    </div>
  );
};

// Preset configurations for common luxury patterns
export const LuxuryPresets = {
  heroTitle: {
    animation: 'luxuryFade',
    duration: 1200,
    easing: 'elegant',
    intensity: 'dramatic',
    delay: 200
  },
  
  premiumCard: {
    animation: 'scaleUp',
    duration: 800,
    easing: 'luxury',
    intensity: 'medium',
    threshold: 0.2
  },
  
  architecturalElement: {
    animation: 'architecturalReveal',
    duration: 1000,
    easing: 'architectural',
    intensity: 'bold',
    threshold: 0.25
  },
  
  refinedContent: {
    animation: 'slideUpFade',
    duration: 900,
    easing: 'refined',
    intensity: 'subtle',
    threshold: 0.15
  },
  
  elegantReveal: {
    animation: 'revealUp',
    duration: 1100,
    easing: 'elegant',
    intensity: 'medium',
    threshold: 0.2
  }
};

export default AnimatedSection;